import React, { useState } from 'react';
import { Lock, Unlock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { jobsService } from '../services';
import { format } from 'date-fns';

/**
 * iter340 v8e — Visual lock badge shown alongside a locked job.
 * Renders nothing when `job.is_locked` is falsy. Tooltip-style title
 * surfaces the audit trail (`Locked by <name> on <date> — <reason>`).
 */
export function LockBadge({ job, lockedByName, className = '' }) {
  if (!job?.is_locked) return null;
  const stamp = job.locked_at ? format(new Date(job.locked_at), 'd MMM yyyy') : null;
  const tip = `Locked${lockedByName ? ` by ${lockedByName}` : ''}${stamp ? ` on ${stamp}` : ''}${job.lock_reason ? ` — ${job.lock_reason}` : ''}`;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-300 ${className}`}
      title={tip}
      data-testid={`lock-badge-${job.id}`}
    >
      <Lock className="h-2.5 w-2.5" />
      Locked
    </span>
  );
}

/**
 * Toggle button — only interactive for Senior Scheduler / Admin.
 * For other roles the button is hidden entirely so non-Schedulers
 * cannot even attempt a lock change. Opens a small dialog asking for
 * an optional reason on lock; clears reason on unlock.
 */
export function LockToggleButton({ job, onChange, size = 'sm', variant = 'outline' }) {
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!job) return null;
  const canToggle = !!(isAdmin || isSeniorScheduler);
  if (!canToggle) return null;

  const isLocked = !!job.is_locked;

  const apply = async (nextState, nextReason) => {
    setBusy(true);
    try {
      const updated = await jobsService.setLock(job.id, {
        is_locked: nextState,
        lock_reason: nextState ? (nextReason || null) : null,
      });
      toast({
        title: nextState ? 'Job locked' : 'Job unlocked',
        description: nextState
          ? 'Other users now need a Senior Scheduler to make changes.'
          : 'Other users can edit this job again.',
      });
      onChange?.(updated);
      setOpen(false);
      setReason('');
    } catch (err) {
      toast({
        title: 'Failed to update lock',
        description: err?.response?.data?.detail || err.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (isLocked) {
    return (
      <Button
        size={size}
        variant={variant}
        className="border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
        onClick={() => apply(false)}
        disabled={busy}
        data-testid={`lock-toggle-${job.id}`}
        title="Unlock this job (Senior Scheduler / Admin only)"
      >
        <Unlock className="h-3.5 w-3.5 mr-1" />
        Unlock
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={() => setOpen(true)}
        disabled={busy}
        data-testid={`lock-toggle-${job.id}`}
        title="Lock this job (Senior Scheduler / Admin only)"
      >
        <Lock className="h-3.5 w-3.5 mr-1" />
        Lock
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-amber-600" /> Lock this job
            </DialogTitle>
            <DialogDescription>
              While locked, only Senior Scheduler / Admin can edit, move, or delete it. Other users will see a message asking them to contact a Senior Scheduler.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="lock-reason">Reason (optional)</Label>
            <Textarea
              id="lock-reason"
              placeholder="e.g. Awaiting client confirmation, locked for billing review…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              rows={3}
              data-testid="lock-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setReason(''); }} disabled={busy}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => apply(true, reason.trim() || null)}
              disabled={busy}
              data-testid="lock-confirm"
            >
              {busy ? 'Locking…' : 'Lock job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Bottom-of-view legend explaining the lock visual so auditors know
 * what the amber border means at a glance.
 */
export function LockLegend({ className = '' }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/50 px-3 py-1.5 text-[11px] text-amber-800 ${className}`}
      data-testid="lock-legend"
    >
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 ring-1 ring-amber-300">
        <Lock className="h-2.5 w-2.5 text-amber-700" />
      </span>
      <span>
        <strong>Locked</strong> — only Senior Scheduler / Admin can modify. Bordered cards show locked items.
      </span>
    </div>
  );
}

/**
 * Returns true when the caught Axios error indicates the backend
 * blocked the action because the job is locked. Used by edit/delete
 * handlers to swap a generic "Failed to save" toast for the precise
 * locked-job message.
 */
export function isLockedError(err) {
  if (!err) return false;
  const status = err?.response?.status;
  if (status !== 403) return false;
  const detail = err?.response?.data?.detail;
  if (typeof detail !== 'string') return false;
  return /job\s+locked/i.test(detail);
}

/**
 * Convenience helper used by toast callers when an edit/delete is
 * rejected with the "Job locked" 403. Pass the toast hook, plus the
 * caught error: it surfaces a consistent message + icon.
 */
export function showLockedToast(toast, err) {
  const detail = err?.response?.data?.detail || err?.message || '';
  const isLockMessage = /lock/i.test(detail);
  toast({
    title: isLockMessage ? 'Job locked' : 'Action blocked',
    description: isLockMessage
      ? 'Job locked. Contact a Senior Scheduler to request a change.'
      : detail || 'Try again',
    variant: 'destructive',
    icon: <AlertTriangle className="h-4 w-4" />,
  });
}
